/* eslint-disable max-lines */
import { ReactNode, useMemo } from 'react';

import { useUtils } from './dateUtils';
import { TableColumn } from 'components/Table';

import RouterLink from 'components/RouterLink/RouterLink';
import { DEFAULT_FORMATS, PickerTypes } from 'components/FormFields/DatePickerFormField';
import strings, { url } from 'constants/index';
import { EnumValue } from 'contexts/EnumValuesContext';
import { ColumnIds } from 'enums';
import { UTCSingleDate } from 'utils/formatter.utils';
import { getDefaultFiltersForType } from 'utils/table.utils.filters';
import { GroupData } from 'features/groups';
export const trueFalsePairs = ['True', 'False'];
export const yesNoPairs = ['yes', 'no'];

export interface EditingRowType {
  id: number;
  columnId: string;
  value: any;
}
export const VenueNameColumn = {
  id: ColumnIds.Venue,
  label: strings.venue,
  filterable: true,
  filters: getDefaultFiltersForType({ type: 'text' }),
  sortable: true,
};
export const VenueIdColumn = {
  id: ColumnIds.VenueId,
  label: strings.venueId,
  filterable: true,
  filters: getDefaultFiltersForType({ type: 'number' }),
  sortable: true,
};

export const getTodayStartISODate = () => {
  const startDate = new Date();
  startDate.setUTCHours(0, 0, 0, 0);

  return startDate.toISOString();
};

export const getOneDayAgoISODate = () => {
  const pastDate = new Date();
  pastDate.setUTCDate(pastDate.getUTCDate() - 1);
  pastDate.setUTCHours(0, 0, 0, 0);

  return pastDate.toISOString();
};

export const getSevenDaysAgoISODate = () => {
  const pastDate = new Date();
  pastDate.setUTCDate(pastDate.getUTCDate() - 7);
  pastDate.setUTCHours(0, 0, 0, 0);

  return pastDate.toISOString();
};

export const getSevenDaysAheadISODate = () => {
  const pastDate = new Date();
  pastDate.setUTCDate(pastDate.getUTCDate() + 7);
  pastDate.setUTCHours(0, 0, 0, 0);

  return pastDate.toISOString();
};

export const SportColumn = {
  id: ColumnIds.SportName,
  label: strings.sport_name,
  filterable: true,
  filters: getDefaultFiltersForType({ type: 'text' }),
  sortable: true,
  formatter: (value, entity) => {
    if (!entity) {
      return value;
    }
    return `[${entity.sport?.id}] ${value}`;
  },
};

export const SportColumnNameOnly = {
  id: ColumnIds.SportName,
  label: strings.sport_name,
  filterable: true,
  filters: getDefaultFiltersForType({ type: 'text' }),
  sortable: true,
  formatter: (value, entity) => {
    if (!entity) {
      return value;
    }
    return `[${entity.sport?.id}] ${value}`;
  },
};

export const IdColumn: TableColumn = {
  id: ColumnIds.Id,
  label: strings.id,
  filterable: true,
  filters: getDefaultFiltersForType({ type: 'number' }),
  sortable: true,
};

export const TeamNameColumn: TableColumn = {
  id: ColumnIds.TeamName,
  label: strings.team_name,
  filterable: true,
  filters: getDefaultFiltersForType({ type: 'text' }),
  sortable: true,
};

export const DrawIdColumn: TableColumn = {
  id: ColumnIds.DrawId,
  label: strings.draw,
  filterable: true,
  filters: getDefaultFiltersForType({ type: 'number' }),
  sortable: true,
};

export const PlayerIdColumn: TableColumn = {
  id: ColumnIds.PlayerId,
  label: strings.player_id,
  filterable: true,
  filters: getDefaultFiltersForType({ type: 'number' }),
  sortable: true,
};

export const IsTroublesomeColumn: TableColumn = {
  id: 'metadataProperties.IsTroublesome',
  label: strings.is_troublesome,
  filterable: true,
  filters: getDefaultFiltersForType({
    type: 'enum',
    values: trueFalsePairs,
  }),
  sortable: true,
};

export const ShirtNumberColumn: TableColumn = {
  id: ColumnIds.ShirtNumber,
  editable: true,
  label: strings.shirt_number,
  type: 'number',
  sortable: true,
};

export const GetPoisitionColumn = (positionMetadata: EntityMetaProperty): TableColumn => ({
  id: ColumnIds.Position,
  editable: true,
  label: strings.position,
  type: 'enum',
  filterable: false,
  sortable: true,
  selectableValuesForEnums: positionMetadata.values,
});

export const IsProtectedColumn: TableColumn = {
  id: ColumnIds.IsProtected,
  label: strings.is_protected,
  filterable: true,
  filters: getDefaultFiltersForType({
    type: 'enum',
    values: trueFalsePairs,
  }),
  sortable: true,
  formatter: (value: boolean) => {
    const stringValue = value.toString();
    return stringValue.charAt(0).toUpperCase() + stringValue.slice(1);
  },
};

export const NameColumn: TableColumn = {
  id: ColumnIds.Name,
  label: strings.name,
  filterable: true,
  filters: getDefaultFiltersForType({ type: 'text' }),
  sortable: true,
};

export const FullNameColumn: TableColumn = {
  id: ColumnIds.FullName,
  label: strings.full_name,
  filterable: true,
  filters: getDefaultFiltersForType({ type: 'text' }),
  sortable: true,
};

export const CompetitorGroupColumn: TableColumn = {
  id: 'competitorGroup.name',
  label: strings.conference_name,
  filterable: true,
  filters: getDefaultFiltersForType({ type: 'text' }),
  sortable: true,
};

export const LocalityNameColumn: TableColumn = {
  id: ColumnIds.LocalityName,
  label: strings.locality,
  filterable: true,
  filters: getDefaultFiltersForType({ type: 'text' }),
  sortable: true,
};

export const getCoachRoleColumn = (contractTypes: Array<EnumValue>): TableColumn => ({
  id: 'ContractRole',
  label: 'Role',
  filterable: false,
  editable: true,
  filters: getDefaultFiltersForType({
    type: 'enum',
    values: contractTypes.map(en => en.name),
  }),
  type: 'enum',
  sortable: false,
  selectableValuesForEnums: contractTypes.map(x => x.name),
});

// export const getTierColumn = (tiers: Array<EnumValue>): TableColumn => ({
//   id: 'tier',
//   label: 'Tier',
//   filterable: false,
//   editable: true,
//   filters: getDefaultFiltersForType({
//     type: 'enum',
//     values: tiers.map(en => en.value.toString()),
//   }),
//   type: 'enum',
//   sortable: true,
//   selectableValuesForEnums: tiers.map(x => x.name),
// });

export const getTierColumn = (
  tiers: Array<{ name: string; value: number | string }>,
): TableColumn => ({
  id: 'tier',
  label: 'Tier',
  filterable: false,
  editable: true,
  filters: [
    {
      type: 'multiselect',
      conditions: [{ key: 'in' }],
      values: tiers.map(en => en.value.toString()),
    },
  ],
  sortable: true,
  selectableValuesForEnums: tiers.map(x => x.name),
});

export const getGenderTypeColumn = (gendertype: Array<EnumValue>): TableColumn => ({
  id: ColumnIds.GenderType,
  label: strings.gender,
  filterable: true,
  filters: getDefaultFiltersForType({
    type: 'enum',
    values: gendertype?.map(en => en.name),
  }),
  sortable: true,
});

export const getGenderColumn = (gendertype: Array<EnumValue>): TableColumn => ({
  id: ColumnIds.Gender,
  label: strings.gender,
  filterable: true,
  filters: getDefaultFiltersForType({
    type: 'enum',
    values: gendertype?.map(en => en.name),
  }),
  sortable: true,
});

export const getAgeCategoryColumn = (ageCategories: Array<EnumValue>): TableColumn => ({
  id: ColumnIds.AgeCategory,
  label: strings.ageCategory,
  filterable: true,
  filters: getDefaultFiltersForType({
    type: 'enum',
    values: ageCategories?.map(en => en.name),
  }),
  sortable: true,
});

export const getRoundTypeColumn = (roundtype: Array<EnumValue>): TableColumn => ({
  id: ColumnIds.Type,
  label: strings.type,
  filterable: true,
  filters: getDefaultFiltersForType({
    type: 'enum',
    values: roundtype?.map(en => en.name),
  }),
  sortable: true,
});

export const getGroupTypeColumn = (groupType: Array<EnumValue>): TableColumn => ({
  id: ColumnIds.Type,
  label: strings.type,
  filterable: true,
  filters: getDefaultFiltersForType({
    type: 'enum',
    values: groupType?.map(en => en.name),
  }),
  sortable: true,
});

export const ParentGroupNameColumn = {
  id: 'parentGroup.name',
  label: strings.parent_group,
  filterable: true,
  filters: getDefaultFiltersForType({ type: 'text' }),
  sortable: true,
  formatter: (value, group: GroupData) => {
    if (!group || value === strings.not_applicable) {
      return value;
    }
    return (
      <RouterLink to={`${url.base_groups}/${group.parentGroup?.id}`}>
        {group.parentGroup?.name}
      </RouterLink>
    );
  },
};

export const getCompetitorStatusTypeColumn = (
  competitorStatusType: Array<EnumValue>,
): TableColumn => ({
  id: 'competitorStatusType',
  label: strings.status,
  filterable: true,
  filters: getDefaultFiltersForType({
    type: 'enum',
    values: competitorStatusType?.map(en => en.name),
  }),
  sortable: true,
});

export const StartDateColumn = {
  id: ColumnIds.StartDate,
  label: strings.start_date,
  filterable: true,
  filters: getDefaultFiltersForType({ type: 'datetime' }),
  sortable: true,
  formatter: (value: string) => UTCSingleDate(value),
};

export const EndDateColumn = {
  id: ColumnIds.EndDate,
  label: strings.end_date,
  filterable: true,
  filters: getDefaultFiltersForType({ type: 'datetime' }),
  sortable: true,
  formatter: (value: string) => UTCSingleDate(value),
};

export const UsernameColumn = {
  id: 'username',
  label: strings.name,
  filterable: true,
  filters: getDefaultFiltersForType({ type: 'text' }),
  sortable: true,
};

export const SportsNamesColumn = {
  id: 'sports',
  label: strings.sports,
  sortable: true,
};

export const priorityTypes = [
  { name: '-100', value: 'Low' },
  { name: '0', value: 'Normal' },
  { name: '100', value: 'High' },
  { name: '200', value: 'Critical' },
];

export const getPriorityColumn = (
  types: Array<(typeof priorityTypes)[number]> = priorityTypes,
): TableColumn => ({
  id: 'priority',
  label: strings.priority,
  filterable: true,
  filters: getDefaultFiltersForType({ type: 'enum', values: types?.map(en => en.name) }),
  sortable: true,
});

export const PermissionsColumn = {
  id: 'permissions',
  label: strings.permissions,
  filterable: false,
  sortable: true,
};

export const GsEntityIdColumn: TableColumn = {
  id: ColumnIds.GsEntityId,
  label: strings.gsEntityId,
  filterable: true,
  filters: getDefaultFiltersForType({ type: 'number' }),
  sortable: true,
};

export const getLocalityCodeColumn = (localityTypes: Array<EnumValue>): TableColumn => ({
  id: 'localityCode',
  label: strings.locality_code,
  filterable: true,
  filters: getDefaultFiltersForType({ type: 'enum', values: localityTypes?.map(en => en.name) }),
  sortable: true,
});

export const SportsColumn = {
  id: ColumnIds.SportName,
  label: strings.sport_name,
  filterable: true,
  filters: [
    {
      ...getDefaultFiltersForType({ type: 'text' })[0],
      label: 'startsWith',
      conditions: [{ key: 'contains' }],
    },
  ],
  formatter: (value, entity) => {
    if (!entity?.sports?.length) {
      return value;
    }
    return entity?.sports.map(({ name }) => name).toString();
  },
};

export const SeasonNameColumn: TableColumn = {
  id: ColumnIds.SeasonName,
  label: strings.season,
  filterable: true,
  filters: getDefaultFiltersForType({ type: 'text' }),
  sortable: true,
};

export const getTextColumn = (id: string, label: string) =>
  ({
    id,
    label,
    filterable: true,
    filters: getDefaultFiltersForType({ type: 'text' }),
    sortable: true,
  }) as TableColumn;

export const CompetitionNameColumn: TableColumn = {
  id: ColumnIds.CompetitionName,
  label: strings.competition,
  filterable: true,
  filters: getDefaultFiltersForType({ type: 'text' }),
  sortable: true,
};

export const CodeColumn: TableColumn = {
  id: ColumnIds.Code,
  label: strings.code,
  filterable: true,
  filters: getDefaultFiltersForType({ type: 'text' }),
  sortable: true,
};

export const CreatedOnColumn = {
  id: ColumnIds.CreatedOn,
  label: strings.created_on,
  filterable: true,
  filters: getDefaultFiltersForType({ type: 'datetime' }),
  sortable: true,
  formatter: (value: string) => UTCSingleDate(value),
};

export const AliasesColumn: TableColumn = {
  id: ColumnIds.Aliases,
  label: strings.aliases,
  filterable: true,
  filters: [
    {
      type: 'text',
      conditions: [{ key: 'equals', label: 'equals' }],
      label: 'equals',
    },
  ],
};

export const getFixtureStatusTypeColumn = (fixturestatustype: Array<EnumValue>): TableColumn => ({
  id: ColumnIds.FixtureStatusType,
  label: strings.fixture_status,
  filterable: true,
  filters: getDefaultFiltersForType({
    type: 'enum',
    values: fixturestatustype?.map(en => en.name),
  }),
  sortable: true,
});

export const ActionsColumn = { id: ColumnIds.Actions, label: strings.actions };
export const DateActionsColumn = { id: ColumnIds.DateActions, label: strings.actions };

export const useFirstRequestCreatedOnColumn = (): TableColumn => {
  const utils = useUtils();

  const firstRequestCreatedOnColumn: TableColumn = useMemo(
    () => ({
      id: ColumnIds.FirstRequestCreatedOn,
      label: strings.firstRequestCreatedOn,
      formatter: value => {
        if (value === strings.not_applicable) {
          return value;
        }
        return utils.format(utils.date(value), DEFAULT_FORMATS[PickerTypes.DateTime]);
      },
    }),
    [utils],
  );

  return firstRequestCreatedOnColumn;
};

export const booleanToStringFormatter = (value: boolean) => {
  const stringValue = value.toString();
  return stringValue.charAt(0).toUpperCase() + stringValue.slice(1);
};

const getValueAtPath = (obj: any, path: string): any =>
  path.split('.').reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);

export const getIdFormatterForEntity =
  (pathname: string, entityKey: string, queryString?: string) => (value: number, entity: any) => {
    if (!entity) {
      return value;
    }

    const resolvedEntity = getValueAtPath(entity, entityKey);
    if (!resolvedEntity || typeof resolvedEntity !== 'object') {
      return value;
    }

    return (
      <RouterLink
        data-testid="show-details"
        to={`${pathname}/${resolvedEntity.id}${queryString ? '?' + queryString : ''}`}
      >
        {resolvedEntity.name}
      </RouterLink>
    );
  };

export const getIdFormatterWithIcon =
  (pathname: string, icon: ReactNode, queryString?: string) => (value: number, entity) => {
    if (!entity || isNaN(value)) {
      return value;
    }

    return (
      <RouterLink
        data-testid="show-details"
        to={`${pathname}/${value}${queryString ? '?' + queryString : ''}`}
      >
        {icon}
      </RouterLink>
    );
  };

export const permissionsFormatter = (_, userAccess) => {
  if (userAccess.isAdmin) {
    return strings.role_curator;
  }
  return `${userAccess.permissions.length} ${strings.permissions}`;
};

export const SportIdsColumn: TableColumn = {
  id: 'sportIds',
  label: `${strings.sport_id}(s)`,
  filterable: true,
  filters: getDefaultFiltersForType({ type: 'number' }),
};

//  const getMatchEntityFormatter = useMemo(
//     () => (value: string, entity) => {
//       if (!entity?.match?.name) {
//         return value;
//       }

//       return (
//         <RouterLink
//           data-testid="show-details"
//           to={`/matching${basePath}/${entity.match.geniusSportsId}`}
//           target="_blank"
//         >
//           {entity.match.name}
//         </RouterLink>
//       );
//     },
//     [basePath],
//   );

export const getIdFormatter =
  (pathname: string, queryString?: string) => (value: number, entity) => {
    if (!entity || isNaN(value)) {
      return value;
    }

    return (
      <RouterLink
        data-testid="show-details"
        to={`${pathname}/${value}${queryString ? '?' + queryString : ''}`}
      >
        {value}
      </RouterLink>
    );
  };

export const getIdForNameFormatter = (entityType: string, value: string, entity: any) => {
  if (!entity) {
    return value;
  }

  return (
    <RouterLink
      data-testid="show-details"
      to={`/competition-hierarchy/${entityType}/${entity?.gsEntityId || entity?.id}`}
      target="_blank"
    >
      {value}
    </RouterLink>
  );
};

export const getSportsFormatter = sports => value => {
  if (Array.isArray(value)) {
    return value.length
      ? value
          .map(id => {
            const sport = sports.find(sport => sport.id === id);
            return `[${id}]  ${sport?.name}`;
          })
          .join('\n')
      : strings.not_applicable;
  }

  return value;
};

export const SourceColumn = {
  id: 'source',
  label: strings.created_by,
  filters: getDefaultFiltersForType({ type: 'text' }),
  alias: 'source',
};

export const SourcesColumn = {
  id: 'sources',
  label: strings.created_by,
  filters: getDefaultFiltersForType({ type: 'text' }),
  alias: 'source',
};

export const MappingIdColumn: TableColumn = {
  id: ColumnIds.Id,
  label: strings.mapping_id,
  filterable: true,
  filters: getDefaultFiltersForType({ type: 'number' }),
  sortable: true,
};

export const useDateOfBirthColumn = (filterable = true, sortable = true): TableColumn => {
  const utils = useUtils();

  const DateOfBirtColumn: TableColumn = useMemo(
    () => ({
      id: ColumnIds.DateOfBirth,
      label: strings.date_of_birth,
      filters: filterable
        ? [
            {
              type: 'date',
              conditions: [
                {
                  key: 'equals',
                },
              ],
              label: 'Equals',
            },
          ]
        : [],
      sortable,
      formatter: value => {
        if (value === strings.not_applicable) {
          return value;
        }

        return utils.format(utils.date(value), DEFAULT_FORMATS[PickerTypes.Date]);
      },
    }),
    [filterable, sortable, utils],
  );

  return DateOfBirtColumn;
};

export const getNestedValue = (record: Record<string, any>, key: string): any => {
  const splitted = key.split('.');
  const currentKey = splitted.shift();

  if (key.length) {
    if (Array.isArray(record)) {
      const value = record.find(p => p.id === currentKey || p.name === currentKey);

      return getNestedValue(value, splitted.join('.'));
    }

    if (typeof record === 'object') {
      return getNestedValue(record[currentKey], splitted.join('.'));
    }
  }

  return record;
};
